/**
 * Get 5-year block label (e.g. "2020-2025") for a given year.
 * @param {string|number} year
 * @returns {string}
 */
function getBlockFromYear(year) {
	const y = parseInt(year, 10);
	if (Number.isNaN(y)) return '';
	const start = y - (y % 5);
	return `${start}-${start + 5}`;
}

export default class TimeLine {
    constructor(element) {
        this.element = element;
        this.timelineYears = element.querySelectorAll('.event__details-link');
        this.eventsButton = element.querySelectorAll('.event__details-link');
        this.eventsPreviews = element.querySelectorAll('.preview__content');
        this.nextEventButton = element.querySelector('.preview__next');
        this.prevEventButton = element.querySelector('.preview__prev');
        this.graphContainer = element.querySelector('.timeline__graph--container');
        this.currentEventId = this.eventsPreviews[0].getAttribute('id').replace('preview-', '');
        this.currentYear = this.eventsPreviews[0].getAttribute('data-year');
        this.currentYearBlock = getBlockFromYear(this.currentYear);
        this.screenWidth = window.innerWidth;

        this.init();
    }

    init() {
        if (this.screenWidth < 1025) {
            this.deactivateEventButtons();
        }

        this.eventsButton.forEach((button) => {
            button.addEventListener('click', (event) => {
                event.preventDefault();
                const eventId = button.getAttribute('href')?.replace('#timeline-event-', '');
                this.currentYear = button.getAttribute('data-year');
                this.currentYearBlock = getBlockFromYear(this.currentYear);

                if (this.screenWidth < 1025) {
                    this.toggleEventView(button);
                } else {
                    this.deactivateEventButtons();
                    button.classList.add('event__details-link--active');
                    this.manageTimelineYearHighlight();

                    this.displayEventContent(eventId);
                    this.scrollGraphToEvent(eventId);
                    this.setArrowsYearLabels();
                }

                this.loadEventVideo(eventId);
            });
        });

        this.nextEventButton.addEventListener('click', () => {
            this.displayNextEvent();
        });

        this.prevEventButton.addEventListener('click', () => {
            this.displayPrevEvent();
        });

        this.setArrowsYearLabels();

        document.addEventListener('keydown', (event) => {
            if (event.key === 'ArrowRight') {
                this.displayNextEvent();
            } else if (event.key === 'ArrowLeft') {
                this.displayPrevEvent();
            }
        });

        // Load the first event video automatically
        this.loadFirstEventVideo();
    }

    loadFirstEventVideo() {
        const firstPreview = this.element.querySelector('.preview__content--show');

        if (firstPreview) {
            this.loadVideoInPreview(firstPreview);
        }
    }

    loadEventVideo(eventId) {
        const previewElement = this.element.querySelector(`#preview-${eventId}`);
        const mobilePreviewElement = this.element.querySelector(`#preview-mobile-${eventId}`);

        this.loadVideoInPreview(previewElement);

        if (mobilePreviewElement) {
            this.loadVideoInPreview(mobilePreviewElement);
        }
    }

    loadVideoInPreview(previewElement) {
        if (!previewElement) {
            return;
        }

        const videoContainer = previewElement.querySelector('.preview__video video');
        const videoSource = videoContainer ? videoContainer.querySelector('source') : null;

        if (!videoContainer || !videoSource) {
            return;
        }

        if (!videoSource.src && videoSource.dataset.src) {
            videoSource.src = videoSource.dataset.src;
            videoContainer.load();
        }

        const playPromise = videoContainer.play();

        if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => {});
        }
    }

    deactivateEventButtons() {
        this.eventsButton.forEach((button) => {
            button.classList.remove('event__details-link--active');
        });
    }

    toggleEventView(eventButton) {
        const eventTile = eventButton.closest('.event__item');
        const eventPreview = eventTile.querySelector('.preview__content--mobile');
        const yearBlock = getBlockFromYear(this.currentYear);
        const yearElement = this.element.querySelector(
            `.highlight-item__year-container[data-year="${yearBlock}"]`
        );

        if (eventTile.classList.contains('event__item--active')) {
            eventTile.classList.remove('event__item--active');
        } else {
            eventTile.classList.add('event__item--active');
        }

        if (eventButton.classList.contains('event__details-link--active')) {
            eventButton.classList.remove('event__details-link--active');
        } else {
            eventButton.classList.add('event__details-link--active');
        }

        if (eventPreview.classList.contains('preview__content--mobile-show')) {
            eventPreview.classList.remove('preview__content--mobile-show');
        } else {
            eventPreview.classList.add('preview__content--mobile-show');
        }

        const activeYearEvents = this.element.querySelectorAll(
            `.event__details-link--active[data-year="${this.currentYear}"]`
        );

        if (yearElement && activeYearEvents.length) {
            yearElement.classList.add('highlight-item__year-container--active');
        } else if (yearElement) {
            yearElement.classList.remove('highlight-item__year-container--active');
        }
    }

    setArrowsYearLabels() {
        const previousElement = this.element.querySelector(
            `#preview-${this.currentEventId}`
        ).previousElementSibling;

        const nextElement = this.element.querySelector(
            `#preview-${this.currentEventId}`
        ).nextElementSibling;

        this.setButtonYear(previousElement, this.prevEventButton);

        this.setButtonYear(nextElement, this.nextEventButton);
    }

    setButtonYear(sibilingElement, buttonElement) {
        const buttonDirection = buttonElement.getAttribute('data-direction');

        const textElement = buttonElement.querySelector(`.preview__${buttonDirection}--year`);

        const position = buttonDirection === "next" ? 'afterbegin' : "beforeend"

        if (!sibilingElement) {
            if (textElement) {
                textElement.remove();
            }
        } else {
            const yearText = sibilingElement.getAttribute('data-year');

            if (!textElement) {
                buttonElement.insertAdjacentHTML(
                    position,
                    `<span class="preview__${buttonDirection}--year">${yearText}</span>`
                );
            } else {
                textElement.innerHTML = yearText;
            }
        }
    }

    displayNextEvent() {
        const nextEvent = this.element.querySelector(
            `#preview-${this.currentEventId}`
        ).nextElementSibling;

        if (nextEvent) {
            const nextEventId = nextEvent.getAttribute('id').replace('preview-', '');
            this.deactivateEventButtons();
            const nextEventButton = this.element.querySelector(`#timeline-event-${nextEventId}`);
            nextEventButton.classList.add('event__details-link--active');
            this.displayEventContent(nextEventId);
            this.scrollGraphToEvent(nextEventId);
            this.currentYear = nextEvent.getAttribute('data-year');
            this.currentYearBlock = getBlockFromYear(this.currentYear);
            this.loadEventVideo(nextEventId);

            this.manageTimelineYearHighlight();
        }

        this.setArrowsYearLabels();
    }

    displayPrevEvent() {
        const prevEvent = this.element.querySelector(
            `#preview-${this.currentEventId}`
        ).previousElementSibling;
        if (prevEvent) {
            const prevEventId = prevEvent.getAttribute('id').replace('preview-', '');

            this.deactivateEventButtons();
            const prevEventButton = this.element.querySelector(`#timeline-event-${prevEventId}`);
            prevEventButton.classList.add('event__details-link--active');

            this.displayEventContent(prevEventId);
            this.scrollGraphToEvent(prevEventId);

            this.currentYear = prevEvent.getAttribute('data-year');
            this.currentYearBlock = getBlockFromYear(this.currentYear);
            this.loadEventVideo(prevEventId);

            this.manageTimelineYearHighlight();
        }

        this.setArrowsYearLabels();
    }

    scrollGraphToEvent(eventId) {
        if (!this.graphContainer) {
            return;
        }

        const targetButton = this.element.querySelector(`#timeline-event-${eventId}`);
        if (!targetButton) {
            return;
        }

        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        targetButton.scrollIntoView({
            behavior: prefersReducedMotion ? 'auto' : 'smooth',
            block: 'center',
            inline: 'nearest'
        });
    }

    manageTimelineYearHighlight() {
        const activeYear = this.element.querySelector('.highlight-item__year-container--active');
        const yearToHighlight = this.element.querySelector(
            `.highlight-item__year-container[data-year="${this.currentYearBlock}"]`
        );

        if (activeYear && activeYear !== yearToHighlight) {
            activeYear.classList.remove('highlight-item__year-container--active');
        }

        if (yearToHighlight && !yearToHighlight.classList.contains('highlight-item__year-container--active')) {
            yearToHighlight.classList.add('highlight-item__year-container--active');
        }
    }

    displayEventContent(eventId) {
        const previewElement = this.element.querySelector(`#preview-${eventId}`);

        this.eventsPreviews.forEach((preview) => {
            preview.classList.remove('preview__content--show');
        });

        previewElement.classList.add('preview__content--show');

        this.currentEventId = eventId;
    }
}
